import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PROJECT_ACCOUNTS_FEATURE_KEY, ProjectAccountsState } from '../reducer/project-accounts.reducer';

const feature = createFeatureSelector<ProjectAccountsState>(PROJECT_ACCOUNTS_FEATURE_KEY);

/** Which closing the slice is currently about. The cache guard compares against this. */
export const selectProjectAccountsProjectId = createSelector(feature, (state) => state.projectId);
export const selectProjectAccounts = createSelector(feature, (state) => state.accounts);
export const selectProjectAccountsStatus = createSelector(feature, (state) => state.accountsStatus);
/** The default view of the list — what the navigation, the overview and the Kontenplan guard read. */
export const selectStepAccounts = createSelector(feature, (state) => state.stepAccounts);
export const selectStepAccountsStatus = createSelector(feature, (state) => state.stepAccountsStatus);

/**
 * What the default list answers for ONE closing: its rows, `null` when the read failed, `undefined`
 * while it has not answered — or answers for another closing. The guard must not read another
 * closing's list as its own.
 */
export const selectStepAccountsFor = (projectId: string) =>
    createSelector(
        selectProjectAccountsProjectId,
        selectStepAccountsStatus,
        selectStepAccounts,
        (loadedFor, status, accounts) =>
            loadedFor !== projectId
                ? undefined
                : status === 'loaded'
                ? accounts
                : status === 'failed'
                ? null
                : undefined,
    );
export const selectProjectAccountsError = createSelector(feature, (state) => state.accountsError);

export const selectClassificationSummary = createSelector(feature, (state) => state.summary);
export const selectClassificationOverdue = createSelector(feature, (state) => state.classificationOverdue);

/** The reconciliation poll's double-start guard (#162). */
export const selectReconciliationPolling = createSelector(feature, (state) => state.reconciliationPolling);
/** Whether the last reconciliation answer had something open. `null` before the first answer. */
export const selectReconciliationOpen = createSelector(feature, (state) => state.reconciliationOpen);
/** Account ids this session expects a reconciliation run for (#162 round 9). */
export const selectReconciliationWatch = createSelector(feature, (state) => state.reconciliationWatch);

/**
 * The period the last ANSWER covered. Labels the current column of figures.
 *
 * Null on a closing with no journal — which is why the sync dialog reads
 * `selectClosingOwnPeriod` instead. Two different questions, and one of them has an answer before
 * any data exists.
 */
export const selectClosingPeriod = createSelector(feature, (state) => state.period);

/** The period the closing COVERS, from the closing itself. Answered before the first sync. */
export const selectClosingOwnPeriod = createSelector(feature, (state) => state.closingPeriod);
export const selectComparisonPeriods = createSelector(feature, (state) => state.comparisons);
export const selectStatementLayouts = createSelector(feature, (state) => state.statementLayouts);
export const selectClosingCurrency = createSelector(feature, (state) => state.currency);
export const selectSelectedComparisonPeriods = createSelector(feature, (state) => state.selectedComparisonPeriods);
export const selectJournalPeriods = createSelector(feature, (state) => state.journalPeriods);
export const selectLastErpSyncAt = createSelector(feature, (state) => state.lastErpSyncAt);
export const selectWithZeroAccounts = createSelector(feature, (state) => state.withZeroAccounts);
export const selectAccountsSearchTerm = createSelector(feature, (state) => state.searchTerm);

/**
 * The comparison periods the screen may OFFER: every journal we hold that ENDS BEFORE this closing
 * starts.
 *
 * Its own period drops out because it is the current column, not a comparison. So does every LATER
 * period, and that one is not cosmetic: a company holds journals for closings after this one too, and
 * offering them let a 2025 closing compare against 2026. The kernel then drops every posting after
 * the balance-sheet date — correctly — so the column arrived full of zeros and read as "nothing was
 * booked in 2026" rather than as "that question makes no sense".
 *
 * With no period of its own known yet, nothing is offered rather than everything: a list that shrinks
 * once the answer arrives is worse than one that fills.
 */
export const selectOfferedComparisonPeriods = createSelector(
    selectJournalPeriods,
    selectClosingPeriod,
    (periods, own) => (own === null ? [] : periods.filter((period) => period.periodEnd < own.periodStart)),
);

/**
 * The offered periods, newest first.
 *
 * Sorted here rather than trusting the repository's order: everything below splits this list on its
 * FIRST member, and a silent reordering upstream would turn the prior year into an optional one.
 */
const selectOfferedNewestFirst = createSelector(selectOfferedComparisonPeriods, (periods) =>
    [...periods].sort((left, right) => right.periodEnd.localeCompare(left.periodEnd)),
);

/**
 * The prior year: the newest period we hold that is not the closing's own.
 *
 * Always compared against, and therefore NOT a chip. An income statement is read against last year —
 * a switch that could turn that column off would let someone read this year's figures as if they
 * stood alone. `null` only when we hold no earlier period at all.
 */
export const selectMandatoryComparisonPeriod = createSelector(
    selectOfferedNewestFirst,
    (periods) => periods[0] ?? null,
);

/**
 * The periods a user may ADD: everything older than the prior year.
 *
 * These are the chips. Each one costs a request — another period is another journal to download and
 * decrypt — which is why they are opt-in and the prior year is not.
 */
export const selectOptionalComparisonPeriods = createSelector(selectOfferedNewestFirst, (periods) => periods.slice(1));

/** The periods that were asked for and came back empty — what the screen reports as missing. */
export const selectMissingComparisonPeriods = createSelector(selectComparisonPeriods, (comparisons) =>
    comparisons.filter((comparison) => comparison.status === 'NO_DATA'),
);

export const selectSyncStatus = createSelector(feature, (state) => state.syncStatus);
export const selectSyncError = createSelector(feature, (state) => state.syncError);
export const selectSyncResult = createSelector(feature, (state) => state.syncResult);

export const selectBindingStatus = createSelector(feature, (state) => state.bindingStatus);
export const selectBindingError = createSelector(feature, (state) => state.bindingError);

/** REV-22005 — the trustee's last account-state write, and its message key when it failed. */
export const selectAccountWriteStatus = createSelector(feature, (state) => state.accountWriteStatus);
export const selectAccountWriteError = createSelector(feature, (state) => state.accountWriteError);
/**
 * Both halves in ONE emission. Two separate selectors combined in a component emit twice per state
 * change — once with the new status and the OLD error — and a host reacting to "failed" would read a
 * stale (or no) reason.
 */
export const selectAccountWrite = createSelector(
    selectAccountWriteStatus,
    selectAccountWriteError,
    (status, error) => ({
        status,
        error,
    }),
);

/**
 * The accounts with no position, in account-number order — what the assignment modal walks through.
 *
 * Derived rather than a second request: the list is already loaded, and a separate call would answer
 * a different moment than the table shows.
 */
export const selectUnassignedAccounts = createSelector(selectProjectAccounts, (accounts) =>
    [...accounts]
        .filter((account) => account.accountGroup === null)
        .sort((left, right) => left.accountNumber.localeCompare(right.accountNumber, undefined, { numeric: true })),
);

/** How many accounts still need a position. Drives the banner above the table. */
export const selectUnassignedCount = createSelector(selectUnassignedAccounts, (accounts) => accounts.length);
